// Kształt odpowiedzi `GET /api/v1/admin/scraper/diagnostics`.
// Kontrakt danych: `biznes-scraper/docs/observability.contract.md`.
//
// Zasada nadrzędna kontraktu: exit code NIE jest źródłem prawdy — dlatego wszędzie, gdzie
// backend nie wie (padnięta agregacja, pusta telemetria), pola `available`/`has_telemetry`
// są `false` i panel MUSI pokazać „brak danych”, a nie zero.

/** Ocena źródła. `unknown` = nie wiemy — nigdy nie renderuj tego jako zdrowia. */
export type Health = "ok" | "warn" | "crit" | "unknown";

/**
 * Status pojedynczego runu — pole `scraper_runs.status` wyliczone przez SCRAPER
 * (`classifyRun` w `src/services/runMetrics.ts`). To jedyne źródło prawdy o tym, czy run
 * coś zdziałał: tylko scraper ma komplet liczników i zna wyjątki źródeł (np. skip-check M4,
 * przez który 0 upsertów przy tysiącach żądań bywa stanem zdrowym). Panel go WYŚWIETLA,
 * nigdy nie przelicza.
 */
export type RunStatus = "running" | "success" | "degraded" | "failed" | "crashed";

/** Rola źródła. `enrichment` = wzbogaca istniejące aukcje, z definicji nie tworzy nowych. */
export type SourceRole = "discovery" | "enrichment";

export interface ScraperSourceHealth {
  /** Klucz ze SCHEDULE runnera, np. `platformaZakupowa`. */
  source: string;
  /** Kanoniczna nazwa (`auctions.source_name`), np. „Platforma Zakupowa”. */
  source_name: string;
  health: Health;
  /** Powody oceny, po polsku, gotowe do wyświetlenia. */
  reasons: string[];
  role: SourceRole;
  /** Czy scraper deklaruje to źródło w manifeście harmonogramu (`scraper_schedule`). */
  in_schedule: boolean;
  /** Wzbogacenia CPV — dowód życia źródeł `enrichment` (one nigdy nie tworzą ogłoszeń). */
  cpv_enriched: number;
  /** Skip-check M4: ile linków zgłoszono / ile Mercury już znał. 0 może też znaczyć
   *  „stary dokument sprzed dołożenia licznika" — pokazujemy wtedy „—". */
  lookup_total: number;
  lookup_known: number;
  /** Pre-filtr source-index nie odpowiedział; scraper wykonał zamiast niego idempotentne upserty. */
  source_index_lookups_failed: number;
  expected_interval_min: number | null;
  last_status: RunStatus | null;
  last_started_at: string | null;
  last_heartbeat_at: string | null;
  last_finished_at: string | null;
  last_exit_code: number | null;
  last_duration_ms: number | null;
  /** Migawka z najnowszego runu, pomocna przy starym heartbeat — nie jest klasyfikatorem. */
  last_activity_kind: string | null;
  last_activity_started_at: string | null;
  last_activity_done_at: string | null;
  /** null = starsza telemetria; 0 = brak aktywnej operacji; >0 = operacja nadal aktywna.
   * „Zakończona” tylko z kind/start/done. */
  last_activity_active_count: number | null;
  last_success_at: string | null;
  last_success_age_min: number | null;
  runs: number;
  runs_failed: number;
  runs_degraded: number;
  created: number;
  revised: number;
  unchanged: number;
  upserts_failed: number;
  requests_total: number;
  requests_ok: number;
  requests_failed: number;
  /** Oczekiwane 401 chronionych endpointów; podzbiór `requests_failed`, nie blokada. */
  requests_auth_required: number;
  requests_blocked: number;
  attachments_failed: number;
  /** Pliki świadomie pominięte przez limit uploadu Mercury (HTTP 413), wymagają obserwacji. */
  attachments_skipped_too_large: number;
  rss_mb_max: number;
  rss_pct_of_limit: number | null;
}

export interface ScraperHealthMatrix {
  /** Czy agregacja `scraper_runs` w ogóle się udała. */
  available: boolean;
  /** Czy w oknie był JAKIKOLWIEK run. `false` = telemetria milczy, nie „wszystko OK”. */
  has_telemetry: boolean;
  /**
   * Manifest harmonogramu (`scraper_schedule`) — lista OCZEKIWANYCH źródeł, publikowana
   * przez scraper przy starcie poda. To ona wyznacza wiersze macierzy, dzięki czemu źródło,
   * które przestało się uruchamiać, jest AWARIĄ, a nie brakiem wiersza.
   *
   * `available: false` (pusty/niedostępny manifest) MUSI renderować się jako „brak danych",
   * nigdy jako „zero oczekiwanych źródeł / wszystko OK".
   */
  schedule: {
    available: boolean;
    expected_sources: number;
    updated_at: string | null;
  };
  window_min: number;
  sources: ScraperSourceHealth[];
  summary: { ok: number; warn: number; crit: number; unknown: number };
  totals: {
    runs: number;
    created: number;
    revised: number;
    upserts_failed: number;
    requests_total: number;
  };
  memory_limit_mb: number;
}

/**
 * Status wiersza rekoncyliacji. `enrichment` = źródło z definicji nie tworzy ogłoszeń
 * (wzbogaca istniejące), więc zero w `auctions` jest jego stanem docelowym — nigdy alarmem.
 */
export type ReconciliationStatus = "ok" | "rozjazd" | "brak_danych" | "enrichment";

export interface ReconciliationRow {
  source_name: string;
  role: SourceRole;
  /** Dla źródeł `enrichment` to TO jest miarą pracy, a nie `telemetry_created`. */
  cpv_enriched: number;
  /** Ile scraper TWIERDZI, że utworzył (z odpowiedzi Mercury). */
  telemetry_created: number;
  /** Ile NAPRAWDĘ przybyło w kolekcji `auctions` — niezależny licznik. */
  auctions_created: number;
  diff: number;
  drift_pct: number;
  status: ReconciliationStatus;
}

export interface ScraperReconciliation {
  available: boolean;
  telemetry_available: boolean;
  auctions_available: boolean;
  rows: ReconciliationRow[];
  drifting_sources: number;
  tolerance: { abs: number; rel: number };
}

export interface ScraperRun {
  source: string;
  status: RunStatus;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  exit_code: number | null;
  created: number;
  revised: number;
  upserts_failed: number;
  rss_mb_max: number;
}

/** Punkt godzinowy z `scraper_rollup_hourly`. */
export interface YieldPoint {
  source: string;
  bucket: string | null;
  runs: number;
  created: number;
  revised: number;
  upserts_failed: number;
  requests_total: number;
  requests_failed: number;
  /** Oczekiwane 401 chronionych endpointów; podzbiór `requests_failed`, nie blokada. */
  requests_auth_required: number;
  requests_blocked: number;
}

export interface ScraperDiagnosticsResponse {
  generated_at: string;
  window: { since: string; until: string; minutes: number };
  health: ScraperHealthMatrix;
  reconciliation: ScraperReconciliation;
  /** Nieobecne wyłącznie przy chwilowej pracy frontu ze starszym backendem. */
  catalog?: ScraperCatalog;
  timeline: {
    available: boolean;
    limit: number;
    truncated: boolean;
    runs: ScraperRun[];
  };
  yield_trend: { available: boolean; days: number; points: YieldPoint[] };
  memory_limit_mb: number;
}

export type ScraperOperationalStatus = "working" | "not_working" | "attention" | "unknown";

export type ScraperCost =
  { kind: "known"; monthlyPln: number; note: string } | { kind: "not-tracked"; reason: string };

export interface ScraperCatalogMetadata {
  acquisition?: {
    methods: ("api" | "html-http" | "browser" | "files-feed")[];
    technology: string;
  };
  scrapedData: string;
  notScrapedData: string;
  dedicatedApi: boolean;
  noApiReason: string | null;
  lastChangedOn: string;
  lastInterventionOn: string;
  resultsScraped: boolean;
  resultsAcquisitionPossible: boolean | null;
  changeNoticesScraped: boolean;
  changeNoticesHandledByPlatform: boolean | null;
  changeNoticesAcquisitionPossible: boolean | null;
  cost: ScraperCost;
}

export interface ScraperCatalogRow {
  status_evaluated_at?: string;
  last_success_at?: string | null;
  last_signal_at?: string | null;
  modes?: {
    source_key: string;
    status: ScraperOperationalStatus;
    last_success_at: string | null;
    last_signal_at: string | null;
    observed_since: string | null;
    assessment_since: string | null;
    business_seconds_without_success: number | null;
  }[];
  source_key: string;
  source_name: string;
  status: ScraperOperationalStatus;
  health: Health;
  status_reasons: string[];
  metadata_complete: boolean;
  catalog: ScraperCatalogMetadata | null;
  last_7_days: number | null;
  previous_month: number | null;
  open_from_previous_month: number | null;
  open_share_pct: number | null;
  cost_per_tender_pln: number | null;
}

export interface ScraperCatalog {
  status_evaluated_at?: string;
  manifest_published_at?: string | null;
  status_policy?: {
    business_hours: number;
    timezone: string;
    calendar: string;
  };
  available: boolean;
  volumes_available: boolean;
  previous_month: { since: string | null; until: string | null };
  total_open_from_previous_month: number | null;
  rows: ScraperCatalogRow[];
}

/** Kolory ocen — spójne w macierzy, timeline i wykresach. */
export const HEALTH_COLORS: Record<Health, string> = {
  ok: "#1F8081", // colorPrimary motywu
  warn: "#D48806",
  crit: "#CF1322",
  unknown: "#8C8C8C",
};

export const HEALTH_LABELS: Record<Health, string> = {
  ok: "OK",
  warn: "Ostrzeżenie",
  crit: "Awaria",
  unknown: "Brak danych",
};

/** Kolory statusów runów — `degraded` celowo czerwone: kod 0 nie znaczy sukces. */
export const RUN_STATUS_COLORS: Record<RunStatus, string> = {
  success: "#1F8081",
  running: "#69B1B2",
  degraded: "#CF1322",
  failed: "#A8071A",
  crashed: "#610B00",
};

export const RUN_STATUS_LABELS: Record<RunStatus, string> = {
  success: "Sukces",
  running: "W toku",
  degraded: "Zdegradowany",
  failed: "Błąd",
  crashed: "Padł (OOM/SIGTERM)",
};
